import { styled, Text } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { ConsentQuestion, Email, Loading, NombreApellidos } from 'src/components';
import icon from 'src/components/img/icon-star-white.svg';
import { FormController, removeWhiteSpace, useFaContent, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { LayoutWrapper } from 'src/feature-app/Screen';
import { formatFlags, formatHeaders, formDataBuilder } from 'src/forms/format-fields';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { GTI50Confirmacion, GTI50ThankYou } from '.';

const ChildrenStyled = styled.div`
	button[aria-label*='aceptar'],
	button[type*='submit'] {
		background-color: #c90010 !important;
		color: #ffffff !important;

		&:hover {
			background-color: #a5000d !important;
		}
	}
	button[aria-label*='omitir'] {
		&:hover {
			background-color: #ffffff !important;
			border: 1px solid #ffffff;
		}
	}
	div > div > div > div > div {
		background: #000000 !important;
	}
	label:hover {
		box-shadow: #c90010 0 0 0 1px !important;
	}
	label:focus-within {
		box-shadow: #c90010 0 0 0 2px !important;
	}
	label:focus-visible {
		outline: none;
	}
`;

const parentStyled = () => {
	if (document.getElementById('oneform-gti50-style')) return;

	const style = document.createElement('style');
	style.id = 'oneform-gti50-style';
	style.innerHTML = `
	#oneform,
	#oneform .close-wrapper,
	#oneform .navigation-wrapper {
		background-color: #000000 !important;
	}
	#oneform .close-wrapper button:hover,
	#oneform .close-wrapper button:focus,
	#oneform .close-wrapper button:active {
		background-color: #c90010 !important;
		border: 1px solid #c90010 !important;
	}
	#oneform .close-wrapper button:focus-visible,
	#oneform .navigation-wrapper button:focus-visible,
	.areYouSureLayer-content button:focus-visible {
		outline: none !important;
	}
	#oneform .navigation-wrapper button {
		background-color: #c90010 !important;
	}
	#oneform .navigation-wrapper button:hover {
		background-color: #a5000d !important;
	}
	#oneform .navigation-wrapper button svg {
		fill: #ffffff !important;
	}
	.areYouSureLayer-content div[style] > button {
		background-color: #c90010;
		border: 1px solid #c90010 !important;
	}
	.areYouSureLayer-content button {
		border: 1px solid #000000 !important;
	}
	.areYouSureLayer-content button:hover,
	.areYouSureLayer-content button:focus,
	.areYouSureLayer-content button:active {
		background-color: #a5000d !important;
		border: 1px solid #a5000d !important;
	}
  `;
	document.head.appendChild(style);
};

export function GTI50ProspectsForm() {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const { formInfo, formData }: OneFormState = store.getState();
	const dispatch = useDispatch();
	const faContent = useFaContent();
	const [loading, setLoading] = useState(true);

	let index = 0;
	const steps: Steps[] = [
		{
			title: <Text>Entérate antes que nadie</Text>,
			fields: (
				<LayoutWrapper>
					<NombreApellidos />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: <Text>Déjanos tu email para que podamos avisarte en cuanto esté disponible</Text>,
			fields: (
				<LayoutWrapper>
					<Email required />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'Email',
			outputs: ['email'],
			outputsText: ['email'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <GTI50Confirmacion />,
			screenIndex: index++,
			name: 'Confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const sendForm = (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, device } = getParamsForm();

			const { formData }: OneFormState = store.getState();
			const { fields, headers, flags } = formData;

			const faContentDpsFields =
				faContent?.dpsFields?.reduce((acc: any, field: any) => {
					acc[field.field] = field.value;
					return acc;
				}, {}) || {};

			const data = {
				...formatHeaders(headers),
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_URL]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
				'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,

				...formatFlags(flags),

				'fields[DPS_BRAND]': 'Volkswagen',
				'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
				'fields[SF_LEAD_SALES_INITIAL_ACTION]': 'Prospect',
				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),

				//'fields[VEH_INTEREST_ID]': '30602',
				//'fields[VEH_INTEREST_MODEL]': 'Golf',

				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',

				...faContentDpsFields,
			};

			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formDataInfo = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formDataInfo);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
		};
	};

	const initialFormData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_PROSPECT_LAUNCH',
			FORM_OBJECTIVE: 'Prospect',
			SF_CAMPAIGN_ID: '7011n000000JrSNAA0',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
		},
		fields: {
			formName: 'prospect',
			formType: 'otherform',
			pageCategory: 'Prospect',
			formularioFlag: 'prospect',
		}, // Analítica
	};

	const { firstStep, lastStep, stepsHistory, numberOfScreens } = getFormInfo();

	const formInfoConfig: Partial<FormInfo> = {
		formStarted: true,
		sendFormCallback: sendForm,
		fullScreen: false,
		lastStep,
		numberOfScreens,
		notALayer: false,
		formTheme: 'inverted',
		formType: 'layer',
		screenIndex: steps[0].screenIndex,
		step: firstStep,
		steps,
		stepsHistory,
		thankYouComponent: <GTI50ThankYou />,
	};

	const rewriteConsentsIcons = () => {
		const imgs = document.querySelectorAll('#oneform img');
		imgs.forEach((img) => {
			(img as HTMLImageElement).src = icon;
		});
	};

	useEffect(() => {
		setLoading(false);
		initializeForm(formInfoConfig, initialFormData);
	}, []);

	useEffect(() => {
		parentStyled();
		rewriteConsentsIcons();
	});

	return loading ? (
		<Loading />
	) : (
		<ChildrenStyled>
			<FormController steps={steps} screenType={'layer-screen'} />
		</ChildrenStyled>
	);
}
